<template>
  <div class="mods-panel">
    <div class="mods-panel__accent" />

    <div class="mods-panel__left">
      <div class="mods-panel__stat-row">
        <div class="mods-panel__stat-head">
          <div class="mods-panel__stat">
            <div class="mods-panel__stat-icon mods-panel__stat-icon--select">
              <v-icon small color="white">mdi-checkbox-marked-outline</v-icon>
            </div>
            <div>
              <div class="mods-panel__stat-label">Выбрано</div>
              <div class="mods-panel__stat-value">
                {{ selected.length }}
                <span v-if="totalDoorphones" class="mods-panel__stat-max">/ {{ totalDoorphones }}</span>
                <span v-else class="mods-panel__stat-max">шт.</span>
              </div>
            </div>
          </div>
          <v-btn
            small
            rounded
            class="text-none mods-panel__select-table"
            color="primary"
            outlined
            :loading="selectingAllFromTable"
            :disabled="!totalDoorphones"
            @click="$emit('select-from-table')"
          >
            <v-icon left small>mdi-table-large</v-icon>
            Все из таблицы
            <span v-if="totalDoorphones" class="ml-1">({{ totalDoorphones }})</span>
          </v-btn>
        </div>
        <v-progress-linear
          :value="selectionPercent"
          height="6"
          rounded
          color="primary"
          background-color="blue lighten-5"
          class="mods-panel__progress"
        />
      </div>

      <div v-if="!selected.length" class="mods-panel__empty-hint">
        <v-icon x-small class="mr-1" color="grey">mdi-information-outline</v-icon>
        Отметьте домофоны RV-3434 в списке или на карте
      </div>

      <div v-if="selectedAll" class="mods-panel__warn mb-2" style="color: #1565c0">
        <v-icon x-small class="mr-1" color="primary">mdi-check-all</v-icon>
        Выбраны все домофоны из таблицы
      </div>

      <div class="mods-panel__actions">
        <v-btn
          small
          rounded
          class="text-none"
          color="primary"
          outlined
          :loading="selectingAll"
          :disabled="!totalDoorphones"
          @click="$emit('select-by-filter')"
        >
          <v-icon left small>mdi-select-all</v-icon>
          Все по фильтру
        </v-btn>
        <v-btn
          small
          rounded
          class="text-none"
          outlined
          :disabled="!selected.length"
          @click="$emit('clear')"
        >
          <v-icon left small>mdi-close-box-outline</v-icon>
          Очистить
        </v-btn>
      </div>

      <mass-schedule-fields v-model="scheduleLocal" class="mt-2" />
    </div>

    <div class="mods-panel__divider" />

    <div class="mods-panel__right">
      <div class="mods-panel__upload-title">
        <v-icon small color="primary" class="mr-1">mdi-image-outline</v-icon>
        Пользовательское изображение (LCD)
      </div>

      <v-file-input
        :value="localImage"
        accept=".bmp"
        outlined
        dense
        hide-details
        label="Файл BMP"
        prepend-icon=""
        prepend-inner-icon="mdi-file-image-outline"
        clearable
        class="border-rounding mb-3"
        @change="onImageChange"
      />

      <div class="mods-panel__cta">
        <v-btn
          color="success"
          class="text-none"
          rounded
          elevation="0"
          :disabled="!canUpload"
          :loading="uploading"
          @click="$emit('upload')"
        >
          <v-icon left small>mdi-cloud-upload-outline</v-icon>
          Запустить процесс
        </v-btn>
        <v-btn
          color="primary"
          class="text-none"
          rounded
          outlined
          :disabled="!selected.length"
          :loading="resetting"
          @click="$emit('reset')"
        >
          <v-icon left small>mdi-restore</v-icon>
          Сброс на дефолтную
        </v-btn>
      </div>
      <div v-if="jobCount > 1" class="mods-panel__jobs-hint">
        <v-icon x-small class="mr-1">mdi-layers-outline</v-icon>
        Будет создано {{ jobCount }} задач по {{ maxCount }} домофонов
      </div>

      <v-alert
        v-if="hasActiveJob"
        dense
        colored-border
        border="left"
        type="info"
        class="mt-3 mb-0"
      >
        <div class="d-flex align-center mb-2">
          <v-icon small class="mr-2" color="primary">mdi-playlist-plus</v-icon>
          Уже есть активные загрузки. Новая партия попадёт в очередь и начнётся после текущих.
        </div>
        <div
          v-for="job in activeJobs"
          :key="'panel-lcd-job-' + job.mass_upload_id"
          class="mods-panel__queue-item"
        >
          <div class="mods-panel__queue-meta">
            <strong>{{ job.mass_upload_id }}</strong>
            <span class="mods-panel__queue-file">{{ job.file_name || 'изображение' }} · {{ job.total }} шт.</span>
            <span class="mods-panel__queue-status">
              {{ job.status === 'running' ? 'выполняется' : 'в очереди' }}
            </span>
          </div>
          <v-btn
            v-if="job.can_cancel || job.status === 'queued'"
            x-small
            text
            color="error"
            class="text-none"
            :loading="!!cancellingJobs[job.mass_upload_id]"
            @click="$emit('cancel-job', job)"
          >
            Отменить
          </v-btn>
        </div>
      </v-alert>

      <div class="mods-panel__hint">
        <v-icon x-small class="mr-1">mdi-monitor</v-icon>
        bmp · 320×200 · 256 цветов · только Rubetek RV-3434 · welcome_display = 3
      </div>
    </div>
  </div>
</template>

<script>
import MassScheduleFields from '@/components/massFunction/components/MassScheduleFields.vue';

export default {
  name: 'MassLcdImageUploadPanel',
  components: { MassScheduleFields },
  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    image: {
      default: null,
    },
    schedule: {
      type: Object,
      required: true,
    },
    uploading: Boolean,
    resetting: Boolean,
    selectingAll: Boolean,
    selectingAllFromTable: Boolean,
    hasActiveJob: Boolean,
    activeJobs: {
      type: Array,
      default: () => [],
    },
    cancellingJobs: {
      type: Object,
      default: () => ({}),
    },
    maxCount: {
      type: Number,
      default: 1000,
    },
    totalDoorphones: {
      type: Number,
      default: 0,
    },
    jobCount: {
      type: Number,
      default: 0,
    },
    selectedAll: Boolean,
  },
  data () {
    return {
      localImage: null,
    };
  },
  computed: {
    scheduleLocal: {
      get () {
        return this.schedule;
      },
      set (val) {
        this.$emit('update:schedule', val);
      },
    },
    selectionPercent () {
      const denom = this.totalDoorphones || this.selected.length || 1;
      return Math.min(100, Math.round((this.selected.length / denom) * 100));
    },
    canUpload () {
      return this.selected.length > 0 && !!this.localImage && this.isScheduleReady;
    },
    isScheduleReady () {
      if (!this.schedule || !this.schedule.useSchedule) return true;
      return !!(this.schedule.startDate && this.schedule.endDate);
    },
  },
  watch: {
    image: {
      immediate: true,
      handler (value) {
        this.localImage = this.normalizeFile(value);
      },
    },
  },
  methods: {
    isFile (value) {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((item) => this.isFile(item));
      }
      return (typeof File !== 'undefined' && value instanceof File) || (typeof Blob !== 'undefined' && value instanceof Blob) || (typeof value === 'object' && typeof value.name === 'string' && Number(value.size) > 0);
    },
    normalizeFile (value) {
      if (!value) return null;
      if (Array.isArray(value)) {
        return value.find((item) => this.isFile(item)) || null;
      }
      return this.isFile(value) ? value : null;
    },
    onImageChange (value) {
      const file = this.normalizeFile(value);
      this.localImage = file;
      this.$emit('update:image', file);
    },
  },
};
</script>

<style scoped>
.mods-panel {
  position: relative;
  display: flex;
  gap: 20px;
  padding: 18px 20px 16px;
  background: #fff;
  border: 1px solid #e3eaf3;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -6px 24px rgba(13, 64, 146, 0.1);
  flex-wrap: wrap;
  overflow: hidden;
}
.mods-panel__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #0d4092 0%, #1e88e5 55%, #26a69a 100%);
}
.mods-panel__left {
  flex: 1 1 360px;
}
.mods-panel__right {
  flex: 1 1 320px;
}
.mods-panel__divider {
  width: 1px;
  background: #e8eef5;
  align-self: stretch;
}
.mods-panel__stat-row {
  margin-bottom: 12px;
}
.mods-panel__stat-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.mods-panel__stat {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mods-panel__stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mods-panel__stat-icon--select {
  background: #0d4092;
}
.mods-panel__stat-label {
  font-size: 12px;
  color: #607d8b;
}
.mods-panel__stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #0d4092;
  line-height: 1.2;
}
.mods-panel__stat-max {
  font-size: 13px;
  font-weight: 400;
  color: #90a4ae;
}
.mods-panel__progress {
  margin-top: 4px;
}
.mods-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.mods-panel__empty-hint,
.mods-panel__warn,
.mods-panel__hint,
.mods-panel__jobs-hint {
  font-size: 12px;
  color: #78909c;
  margin-top: 8px;
}
.mods-panel__upload-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #0d4092;
  display: flex;
  align-items: center;
}
.mods-panel__cta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.mods-panel__queue-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-top: 1px solid #e8eef5;
}
.mods-panel__queue-meta {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}
.mods-panel__queue-file {
  color: #607d8b;
}
.mods-panel__queue-status {
  color: #1565c0;
}
@media (max-width: 960px) {
  .mods-panel__divider {
    display: none;
  }
}
</style>
